import React, { useEffect } from 'react';
import { Dialog, DialogContent, DialogClose, DialogTitle } from '@/components/ui/dialog';
import useModalStore from '@/stores/useModalStore';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import IconCross from '@/assets/icons/cross.svg';
import { trackEvent } from '@/utils/analytics';
import { EventAction } from '@/types/analytics';
import { getIconBadge } from '@/pages/XDao/ProjectsPage/utils/iconBadge';

const ImageModal: React.FC = () => {
    const { imageName, showImageModal, imageUrl, imageRarity, setShowImageModal } = useModalStore();

    const handleClose = () => {
        setShowImageModal(false);
    };

    // Clear imageUrl after modal closes and animation completes
    useEffect(() => {
        if (!showImageModal && imageUrl) {
            // Wait for exit animation (150ms) before clearing
            const timer = setTimeout(() => {
                // Force clear imageUrl by calling with null
                useModalStore.setState({ imageUrl: null });
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [showImageModal, imageUrl]);

    const handleModalClose = (reason: 'cross' | 'backdrop') => {
        trackEvent('ui_interaction', EventAction.MODAL_CLOSE, {
            label: `image_modal_${reason}`,
            component: 'image_modal',
        });
    };
    
    // Don't render if never opened
    if (!imageUrl && !showImageModal) return null;

    return (
        <Dialog open={showImageModal} onOpenChange={handleClose}>
            <DialogContent
                className="max-w-[95vw] max-h-[95vh] w-auto h-auto bg-[#171921] border border-gray-800 text-white px-7 py-9 rounded-2xl overflow-hidden"
                shouldShowCloseButton={false}
                onInteractOutside={() => handleModalClose('backdrop')}
            >
                {/* Dummy Title */}
                <VisuallyHidden>
                    <DialogTitle></DialogTitle>
                </VisuallyHidden>

                {/* Header */}
                <div className="flex flex-row items-center justify-between mb-4">
                    <h2 className="text-xl font-inter font-semibold text-white">
                        NFT
                    </h2>
                    <DialogClose asChild>
                        <button
                            className="outline-none"
                            onClick={() => handleModalClose('cross')}
                        >
                            <img
                                src={IconCross}
                                alt="cross"
                                className="w-[1.88rem] h-[1.88rem]"
                            />
                        </button>
                    </DialogClose>
                </div>

                {/* Image */}
                <div className="relative">
                    <img
                        src={imageUrl || ''}
                        alt="Fullscreen view"
                        className="max-w-[calc(95vw-3.5rem)] max-h-[calc(95vh-8rem)] object-contain rounded-lg"
                    />
                    
                    <span className="absolute top-4 left-4 text-white text-xl font-inter font-medium">
                        {imageName || ''}
                    </span>
                    
                    {imageRarity && (
                        <div className="w-[1.875rem] h-[1.875rem] absolute top-4 right-4">
                            <img
                                src={getIconBadge(imageRarity)}
                                alt={imageRarity}
                                className="w-full h-full object-contain"
                            />
                        </div> 
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ImageModal;
